import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, InlineNotification, SearchField, SegmentedControl, Table, useLocalizeMessage, } from 'libs.nucleus';
import { FamilyExpandContext, FamilyExpandToggle } from './family_expand_toggle.component';
import { CONSENT_LIST_STATUS_FILTERS, groupConsentTemplateRows, lineageHasDraft, } from './group_consent_template_rows';
import { ConsentTemplateSourcePreviewModal } from '../../components/consent_template_source_preview_modal.component';
import { CONSENT_MANAGER_UPLOAD_DESCRIPTION, ConsentUploadZone } from '../components/consent_upload_zone.component';
import { ConfirmArchivalModal } from '../confirm_archival_modal/confirm_archival_modal.component';
import { useConsentTableColumns } from '../hooks/use_consent_table_columns.hook';
const STATUS_FILTER_LABELS = {
    all: 'All',
    published: 'Published',
    draft: 'Draft',
    archived: 'Archived',
};
const getConsentRowId = (row) => row.id;
const CONSENT_TEMPLATE_PAGE_SIZE = 50;
// Nucleus Table uses border-separate and paints borders on <td>, so a <tr> left border never shows.
// Stretch into the first cell's padding (px-6 / py-2) and paint a 3px inset bar on the row edge.
const DRAFT_ROW_ACCENT_STYLE = {
    marginLeft: -24,
    marginTop: -8,
    marginBottom: -8,
    paddingLeft: 24,
    paddingTop: 8,
    paddingBottom: 8,
    boxShadow: 'inset 3px 0 0 0 var(--n-warning-600)',
};
const showsDraftRowAccent = (row, expandedFamilyIds) => (row.status === 'Draft' && !row.familyParentId) || Boolean(row.expandableFamily && expandedFamilyIds.has(row.id));
// Sibling versions stay in this table so they share the parent header and column widths.
// Nucleus expandableContent wraps a nested Table, which re-paints thead and cannot line up.
const rowsForFamilies = (pageFamilies, expandedFamilyIds) => pageFamilies.flatMap(({ parent, siblings }) => {
    if (siblings.length === 0) {
        return [parent];
    }
    const expandableParent = {
        ...parent,
        expandableFamily: true,
        familySiblingIds: siblings.map((sibling) => sibling.id),
    };
    if (!expandedFamilyIds.has(parent.id)) {
        return [expandableParent];
    }
    return [expandableParent, ...siblings.map((sibling) => ({ ...sibling, familyParentId: parent.id }))];
});
export const ConsentManagerUI = ({ files, onFilesUploaded, onEdit, onEditRow, onViewConfiguration, archiveTemplateVersion: removeFileFromStorage, onNewVersionRequest, deleteTemplateVersion, canEditTemplate, canViewTemplateSource, canArchiveTemplate, canDeleteTemplate, canCreateTemplate, isDocGenEconsentEnabled = false, getArchiveImpact, isLoading = false, hasLoadError = false, onRetryLoad, templateTotal, onPageIndexChange, onListQueryChange, }) => {
    const translate = useLocalizeMessage();
    const [statusFilterIndex, setStatusFilterIndex] = useState(0);
    const [searchValue, setSearchValue] = useState('');
    const [viewSourceDocumentId, setViewSourceDocumentId] = useState(null);
    const [archiveConfirmRow, setArchiveConfirmRow] = useState(null);
    const [expandedFamilyIds, setExpandedFamilyIds] = useState(() => new Set());
    const [pagination, setPagination] = useState({
        pageIndex: 0,
        pageSize: CONSENT_TEMPLATE_PAGE_SIZE,
    });
    const toggleFamily = useCallback((id) => {
        setExpandedFamilyIds((current) => {
            const next = new Set(current);
            if (next.has(id)) {
                next.delete(id);
            }
            else {
                next.add(id);
            }
            return next;
        });
    }, []);
    const statusFilterItems = useMemo(() => CONSENT_LIST_STATUS_FILTERS.map((id) => ({ id, label: translate(STATUS_FILTER_LABELS[id]) })), [translate]);
    const statusFilter = CONSENT_LIST_STATUS_FILTERS[statusFilterIndex] ?? 'all';
    const families = useMemo(() => groupConsentTemplateRows(files, statusFilter, searchValue), [files, statusFilter, searchValue]);
    const hasDraftInLineage = useCallback((templateKey) => lineageHasDraft(files, templateKey), [files]);
    useEffect(() => {
        onListQueryChange?.({ search: searchValue, status: statusFilter });
    }, [searchValue, statusFilter, onListQueryChange]);
    const dataColumns = useConsentTableColumns({
        onEdit,
        onEditRow,
        onViewConfiguration,
        onViewSource: setViewSourceDocumentId,
        removeFileFromStorage,
        deleteTemplateVersion,
        onNewVersionClick: onNewVersionRequest && canCreateTemplate ? onNewVersionRequest : undefined,
        canEditTemplate,
        canViewTemplateSource,
        canArchiveTemplate,
        canDeleteTemplate,
        canCreateTemplate,
        isDocGenEconsentEnabled,
        onConfirmArchival: isDocGenEconsentEnabled ? setArchiveConfirmRow : undefined,
        hasDraftInLineage,
    });
    const familyExpand = useMemo(() => ({ expandedFamilyIds, toggleFamily, translate }), [expandedFamilyIds, toggleFamily, translate]);
    // Chevron lives in the Title cell: nucleus treats cell 0 as the primary/medium label.
    const columns = useMemo(() => {
        const [titleColumn, ...rest] = dataColumns;
        if (!titleColumn) {
            return dataColumns;
        }
        return [
            {
                ...titleColumn,
                cell: (context) => {
                    const titleCell = titleColumn.cell;
                    const row = context.row.original;
                    const showAccent = showsDraftRowAccent(row, expandedFamilyIds);
                    return (_jsxs("div", { className: 'nucleus-relative nucleus-flex nucleus-h-full nucleus-items-center nucleus-gap-2 nucleus-min-w-0', "data-testid": showAccent ? 'consent-draft-row-accent' : undefined, style: showAccent ? DRAFT_ROW_ACCENT_STYLE : undefined, children: [_jsx(FamilyExpandToggle, { row: row }), typeof titleCell === 'function' ? titleCell(context) : null] }));
                },
            },
            ...rest,
        ];
    }, [dataColumns, expandedFamilyIds]);
    // Nucleus resets to page 1 on every new `data` identity. Expand rebuilds the page rows, so
    // without owned pagination a chevron on page 2+ looks like a filter change and jumps the viewport.
    useEffect(() => {
        setPagination((current) => {
            if (current.pageIndex === 0) {
                return current;
            }
            onPageIndexChange?.(0);
            return { ...current, pageIndex: 0 };
        });
    }, [statusFilter, searchValue, onPageIndexChange]);
    const pageCount = Math.max(1, Math.ceil((templateTotal ?? families.length) / pagination.pageSize));
    useEffect(() => {
        setPagination((current) => {
            if (current.pageIndex <= pageCount - 1) {
                return current;
            }
            onPageIndexChange?.(pageCount - 1);
            return { ...current, pageIndex: pageCount - 1 };
        });
    }, [pageCount, onPageIndexChange]);
    // Local files are already one Library page when templateTotal is set. Slice families only for
    // in-memory lists (tests and any caller that still passes the full catalog).
    const pagedRows = useMemo(() => {
        if (templateTotal !== undefined) {
            return rowsForFamilies(families, expandedFamilyIds);
        }
        const start = pagination.pageIndex * pagination.pageSize;
        return rowsForFamilies(families.slice(start, start + pagination.pageSize), expandedFamilyIds);
    }, [families, expandedFamilyIds, pagination, templateTotal]);
    const handlePaginationChange = useCallback((updater) => {
        setPagination((current) => {
            const next = typeof updater === 'function' ? updater(current) : updater;
            if (next.pageIndex !== current.pageIndex) {
                onPageIndexChange?.(next.pageIndex);
            }
            return next;
        });
    }, [onPageIndexChange]);
    return (_jsxs("div", { className: 'nucleus-flex nucleus-flex-col', children: [canCreateTemplate && (_jsx(ConsentUploadZone, { onFileAdd: onFilesUploaded, description: translate(CONSENT_MANAGER_UPLOAD_DESCRIPTION) })), _jsx(ConsentTemplateSourcePreviewModal, { isOpen: viewSourceDocumentId !== null, documentId: viewSourceDocumentId, onClose: () => setViewSourceDocumentId(null) }), isDocGenEconsentEnabled && getArchiveImpact && (_jsx(ConfirmArchivalModal, { isOpen: archiveConfirmRow !== null, resourceId: archiveConfirmRow?.id ?? '', templateKey: archiveConfirmRow?.templateKey ?? '', getArchiveImpact: getArchiveImpact, onConfirm: async () => {
                    if (!archiveConfirmRow) {
                        return;
                    }
                    await removeFileFromStorage(archiveConfirmRow.id);
                }, onClose: () => setArchiveConfirmRow(null) })), hasLoadError ? (_jsxs("div", { className: 'nucleus-flex nucleus-flex-col nucleus-items-start nucleus-gap-4', children: [_jsx(InlineNotification, { type: 'error', title: translate("Couldn't load consent templates"), subtitle: translate('The template list is unavailable. This is a loading failure, not an empty study.') }), onRetryLoad && (_jsx(Button, { label: translate('Try again'), onClick: onRetryLoad, dataTestId: 'consent-templates-retry' }))] })) : (_jsx(FamilyExpandContext.Provider, { value: familyExpand, children: _jsx(Table, { columns: columns, data: pagedRows, getRowId: getConsentRowId, isLoading: isLoading, hasPagination: true, manualPagination: true, pageCount: pageCount, initialPageSize: CONSENT_TEMPLATE_PAGE_SIZE, manualPaginationState: pagination, onPaginationChange: handlePaginationChange, paginationNavigationScreenReaderLabel: translate('Consent template pages'), getRowCustomClasses: (row) => (row.familyParentId ? 'nucleus-bg-neutral-50' : ''), labelNoResults: translate(searchValue.trim() || statusFilter !== 'all'
                        ? 'No matching consent templates'
                        : 'No consent files have been added'), customHeaderContent: _jsxs("div", { className: 'nucleus-flex nucleus-items-end nucleus-gap-4 nucleus-w-full', children: [_jsx(SegmentedControl, { items: statusFilterItems, activeItemIndex: statusFilterIndex, onItemClick: (_, index) => setStatusFilterIndex(index) }), _jsx(SearchField, { ariaLabel: translate('Search'), placeholder: translate('Search...'), value: searchValue, onChange: (e) => setSearchValue(e.target.value), onClear: () => setSearchValue(''), width: 'base', dataTestId: 'consent-search' })] }) }) }))] }));
};
